import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import * as signalR from '@microsoft/signalr';
import {HubConnection, HubConnectionState} from '@microsoft/signalr';
import {AuthenticationService} from '../../../_auth/authentication.service';

/**
 * A device redeemed an enrolment session (#279). Mirrors the server's `DeviceEnrollmentRedeemedDto`, pushed on the
 * `EnrollmentRedeemed` hub method to the shard's device-information group.
 *
 * It carries **no secret** — the session id is already in the QR, the tenant ids only scope the event, and the
 * serial is the device that connected. The onboarding screen matches {@link sessionId} against the code it is
 * showing; on a match it stops rotating the now-spent code and waits for the outcome (see
 * {@link DeviceEnrollmentCompletedEvent}).
 */
export interface DeviceEnrollmentRedeemedEvent {
  /** The redeemed session's id — the value the screen compares against the session it is displaying. */
  sessionId: string;
  /** The company the session belonged to. Scoping/diagnostic only. */
  companyId: string;
  /** The shard the session belonged to. */
  shardId: string;
  /** The serial number of the device that redeemed the session — shown while it is being set up. */
  serialNumber: string;
}

/**
 * Where the name a freshly enrolled device carries came from (#647). Mirrors the server's
 * `EnrollmentDeviceNameOrigin`, resolved in the login tail from whether the device row existed beforehand and
 * whether it adopted a same-serial predecessor's name.
 *
 * The three values are the whole reason this event exists: "the name you see was inherited from the iPad this
 * one replaced" and "this device has no name yet" look identical on screen unless the server says which it is.
 */
export type EnrollmentDeviceNameOrigin = 'New' | 'Inherited' | 'Existing';

/**
 * The enrolment **finished** — the register holds a token (#647). Mirrors `DeviceEnrollmentCompletedDto`, pushed
 * on `EnrollmentCompleted` from the login tail, after the device row exists and its name has been resolved.
 *
 * Why a second push rather than more fields on {@link DeviceEnrollmentRedeemedEvent}: the redeem is stamped
 * *before* the login funnel runs, so at that moment there is no device row, no name and no outcome. Everything
 * this event carries simply does not exist yet when the code is consumed.
 *
 * Carries no secret: {@link deviceId} is the device's own id (already on the device list), and the name is the
 * label an administrator picked.
 */
export interface DeviceEnrollmentCompletedEvent {
  /** The session the device redeemed — matched against the session the screen was showing. */
  sessionId: string;
  /** The company the session belonged to. Scoping/diagnostic only. */
  companyId: string;
  /** The shard the session belonged to. */
  shardId: string;
  /** The serial number of the device that was set up. */
  serialNumber: string;
  /** The device's id — what the inline rename posts to `Device/Information/ChangeName`. */
  deviceId: string;
  /** The device's name. Empty (or the device id) when the device is new and unnamed — see {@link nameOrigin}. */
  name: string;
  /** Where {@link name} came from, which decides the sentence the success card shows. */
  nameOrigin: EnrollmentDeviceNameOrigin;
}

/**
 * The enrolment **failed after the code was already redeemed** (#647). Mirrors `DeviceEnrollmentFailedDto`,
 * pushed on `EnrollmentFailed` when the login funnel refuses behind a successful redeem — a contract device cap,
 * a TSS refusal, a provider outage on first registration.
 *
 * This closes a real gap: before it, that screen still read "Gerät erfolgreich verbunden" while the register
 * showed an error and the code was spent. The operator saw success where there was none.
 */
export interface DeviceEnrollmentFailedEvent {
  /** The session the device redeemed — matched against the session the screen was showing. */
  sessionId: string;
  /** The company the session belonged to. Scoping/diagnostic only. */
  companyId: string;
  /** The shard the session belonged to. */
  shardId: string;
  /** The serial number of the device that tried to set itself up. */
  serialNumber: string;
  /**
   * The structured `ApiError.code`, or the name of a legacy `ResponseErrors` member for the refusals that still
   * answer with a bare enum (`MaxDeviceCountReached`). Mapped to German by `enrollmentFailureMessage`.
   */
  errorCode: string;
  /**
   * The server's English, developer-facing explanation. Carried for logs and diagnostics and **never rendered** —
   * see the style guide's *Surfacing an API error*.
   */
  description: string;
}

/**
 * Everything the onboarding screen can be told about a session, tagged so a single subscription can carry all
 * three without the component having to guess which shape it received.
 *
 * The tag is added **here**, on the way out of the hub, rather than being a field the server sends: the wire
 * shapes mirror three distinct DTOs, and inventing a discriminator on the server would mean the DTOs could drift
 * from the hub method names that actually route them.
 */
export type DeviceEnrollmentEvent =
  | ({kind: 'redeemed'} & DeviceEnrollmentRedeemedEvent)
  | ({kind: 'completed'} & DeviceEnrollmentCompletedEvent)
  | ({kind: 'failed'} & DeviceEnrollmentFailedEvent);

/**
 * Listens for the life cycle of an enrolment session over SignalR, so the register-onboarding screen can react
 * the instant a code is consumed (#279) and then report what actually became of it (#647) — instead of leaving a
 * dead code rotating for someone to rescan into an "invalid token" error, or claiming success for a login that
 * failed behind the redeem.
 *
 * ### Three events, one connection
 *
 * `EnrollmentRedeemed` says the code is spent (it is stamped the moment the session is consumed, before the login
 * funnel runs, which is why it can say nothing about the device). `EnrollmentCompleted` and `EnrollmentFailed`
 * are pushed from the login tail and are mutually exclusive: exactly one of them follows a redeem, and it is the
 * only thing that may turn the screen green or red. All three ride the **same** `/hubs/devices` connection and
 * the same shard-scoped group — no new hub, no new group, one `listen()`.
 *
 * ### Why this hub, and how it is tenant-scoped
 *
 * It reuses the **same `/hubs/devices` connection the device list uses** — connect, invoke `JoinListeners` (which
 * joins the caller's shard-scoped device-information group), then receive. The group id embeds the shard resolved
 * from the session's own token, so an event for one shard is delivered only to operators viewing that shard — it
 * can never reach another tenant.
 *
 * ### Lifecycle
 *
 * {@link listen} returns a cold observable: it builds the connection when subscribed and stops it on teardown, so
 * the component's `takeUntilDestroyed` is all that is needed to clean up. Nothing here is persisted — the events
 * carry no secret and are not written anywhere.
 */
@Injectable({providedIn: 'root'})
export class DeviceEnrollmentRealtimeService {
  constructor(private authenticationService: AuthenticationService, @Inject('BASE_URL') private baseUrl: string) {
  }

  /**
   * Emits every enrolment event for the caller's shard for as long as the returned observable is subscribed. The
   * connection is created lazily on subscribe and torn down on unsubscribe.
   */
  listen(): Observable<DeviceEnrollmentEvent> {
    return new Observable<DeviceEnrollmentEvent>(subscriber => {
      const connection = this.createConnection();

      connection.on('EnrollmentRedeemed',
        (event: DeviceEnrollmentRedeemedEvent) => subscriber.next({kind: 'redeemed', ...event}));
      connection.on('EnrollmentCompleted',
        (event: DeviceEnrollmentCompletedEvent) => subscriber.next({kind: 'completed', ...event}));
      connection.on('EnrollmentFailed',
        (event: DeviceEnrollmentFailedEvent) => subscriber.next({kind: 'failed', ...event}));

      // Join the shard's device-information group once connected, and re-join after an automatic reconnect —
      // otherwise a dropped-and-restored connection would go on receiving nothing.
      const joinListeners = () => connection.invoke('JoinListeners').catch(err => console.error(err));
      connection.onreconnected(() => joinListeners());

      connection.start()
        .then(() => joinListeners())
        .catch(err => console.error(err));

      return () => {
        if (connection.state !== HubConnectionState.Disconnected) {
          connection.stop().catch(err => console.error(err));
        }
      };
    });
  }

  /**
   * Builds the hub connection {@link listen} drives.
   *
   * It is a separate, overridable member purely as a **test seam**: `HubConnectionBuilder` reaches for a real
   * WebSocket the moment `start()` is called, so a spec proving that the three handlers are registered (and that
   * each emits with the right `kind`) has to substitute the connection, not the whole service — substituting the
   * service would leave exactly the wiring under test unexercised.
   */
  protected createConnection(): HubConnection {
    return new signalR.HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.Warning)
      .withUrl(this.baseUrl + '/hubs/devices', {
        withCredentials: true,
        accessTokenFactory: () => this.authenticationService.userValue!.jwt
      })
      .withAutomaticReconnect()
      .build();
  }
}
